/* eslint-disable react-hooks/rules-of-hooks */
import { useEffect, useRef } from 'react';
import { fromEvent, merge } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { TASK_CONST } from '@/constant';
import { useUpdateWorkTimeMutation } from '@/hook';
import { isInputElement, useSubscriptions, useUnleash } from '@/package';
import { useAuthStore, useTaskFrameStore } from '@/store';
import type { FrameData } from '@/type';

type WorkSession = {
  startTime: number;
  thresholdSecond: number;
  firstActionTime: Nullish<number>;
  workType: TASK_CONST.TASK_FRAME_WORK_TYPE;
};

const THRESHOLD_SECOND = {
  REVIEW: 30,
  WORK: 60,
  REWORK: 30,
};

const getInitWorkSession = (frame: FrameData): Nullish<WorkSession> => {
  if (frame.workState === TASK_CONST.TASK_FRAME_WORKSTATE.REVIEWING) {
    return {
      firstActionTime: null,
      startTime: Date.now(),
      thresholdSecond: THRESHOLD_SECOND.REVIEW,
      workType: TASK_CONST.TASK_FRAME_WORK_TYPE.REVIEW,
    };
  }

  if (frame.workState !== TASK_CONST.TASK_FRAME_WORKSTATE.WORKING) {
    return null;
  }

  const isRework = frame.rejectCount > 0;

  return {
    firstActionTime: null,
    startTime: Date.now(),
    thresholdSecond: isRework ? THRESHOLD_SECOND.REWORK : THRESHOLD_SECOND.WORK,
    workType: isRework
      ? TASK_CONST.TASK_FRAME_WORK_TYPE.REWORK
      : TASK_CONST.TASK_FRAME_WORK_TYPE.WORK,
  };
};

const getMeasuredStartTime = (workSession: WorkSession): number => {
  if (workSession.firstActionTime == null) {
    return workSession.startTime;
  }

  const firstActionDiff = workSession.firstActionTime - workSession.startTime;

  if (firstActionDiff <= workSession.thresholdSecond * 1000) {
    return workSession.startTime;
  }

  return workSession.firstActionTime;
};

const isReviewTerminalTransition = (
  prevWorkState: Nullish<FrameData['workState']>,
  workState: FrameData['workState'],
) => {
  if (prevWorkState !== TASK_CONST.TASK_FRAME_WORKSTATE.REVIEWING) {
    return false;
  }

  return (
    workState === TASK_CONST.TASK_FRAME_WORKSTATE.DONE ||
    workState === TASK_CONST.TASK_FRAME_WORKSTATE.REJECT
  );
};

const isSubmitTransition = (
  prevWorkState: Nullish<FrameData['workState']>,
  workState: FrameData['workState'],
) => {
  return (
    prevWorkState === TASK_CONST.TASK_FRAME_WORKSTATE.WORKING &&
    workState === TASK_CONST.TASK_FRAME_WORKSTATE.REVIEW
  );
};

const useWorkTimeTracker = () => {
  const { isEnabled } = useUnleash();

  if (!isEnabled('vuex.sapporo.workTimeEnabled')) return;

  const currentFrame = useTaskFrameStore(state => state.currentFrame);
  const prevFrameSeqRef = useRef<Nullish<FrameData['frameSeq']>>(null);
  const prevTaskIDRef = useRef<Nullish<FrameData['taskID']>>(null);
  const prevWorkStateRef = useRef<Nullish<FrameData['workState']>>(null);
  const workSessionRef = useRef<Nullish<WorkSession>>(null);
  const { mutateAsync: updateWorkTime } = useUpdateWorkTimeMutation();

  const flushWorkTime = ({
    reason,
    frameSeq,
    taskID,
  }: {
    reason: string;
    frameSeq: FrameData['frameSeq'];
    taskID: FrameData['taskID'];
  }) => {
    if (workSessionRef.current == null)
      return Promise.reject(new Error(`No action occurred yet`));

    const measuredStartTime = getMeasuredStartTime(workSessionRef.current);
    const workTime = Math.max(
      0,
      Math.floor((Date.now() - measuredStartTime) / 1000),
    );

    if (workTime < 1)
      return Promise.reject(new Error('`workTime` must be greater than `0`'));

    const startTime = new Date(measuredStartTime).toISOString();
    const userId = useAuthStore.getState().user.id;
    const workType = workSessionRef.current.workType;

    if (import.meta.env.DEV) {
      console.log('[useWorkTimeTracker] flushWorkTime', {
        frameSeq,
        reason,
        startTime,
        taskID,
        workTime,
        workType,
      });
    }

    return updateWorkTime({
      frameSeq,
      startTime,
      taskID,
      userId,
      workTime,
      workType,
    });
  };

  const trackFirstAction = () => {
    if (workSessionRef.current == null) return;
    if (workSessionRef.current.firstActionTime != null) return;

    workSessionRef.current.firstActionTime = Date.now();
  };

  useSubscriptions(
    () => [
      merge(
        fromEvent(window, 'scroll'),
        fromEvent(window, 'wheel'),
        fromEvent(window, 'pointerdown'),
        fromEvent(window, 'pointermove'),
        fromEvent(window, 'pointerup'),
      )
        .pipe(tap(trackFirstAction))
        .subscribe(),
      fromEvent<KeyboardEvent>(window, 'keydown')
        .pipe(
          filter(event => !isInputElement(event.target)),
          filter(event => event.defaultPrevented),
          tap(trackFirstAction),
        )
        .subscribe(),
    ],
    [],
  );

  useEffect(() => {
    if (currentFrame == null) {
      prevFrameSeqRef.current = null;
      prevTaskIDRef.current = null;
      prevWorkStateRef.current = null;
      workSessionRef.current = null;
      return;
    }

    const { frameSeq, taskID, workState } = currentFrame;
    const prevFrameSeq = prevFrameSeqRef.current;
    const prevTaskID = prevTaskIDRef.current;
    const prevWorkState = prevWorkStateRef.current;

    const isFrameChanged = prevFrameSeq !== frameSeq;

    if (isFrameChanged) {
      if (
        prevFrameSeq != null &&
        prevTaskID != null &&
        (prevWorkState === TASK_CONST.TASK_FRAME_WORKSTATE.WORKING ||
          prevWorkState === TASK_CONST.TASK_FRAME_WORKSTATE.REVIEWING)
      ) {
        flushWorkTime({
          reason: 'frameChanged',
          frameSeq: prevFrameSeq,
          taskID: prevTaskID,
        });
      }

      workSessionRef.current = getInitWorkSession(currentFrame);
      prevFrameSeqRef.current = frameSeq;
      prevTaskIDRef.current = taskID;
      prevWorkStateRef.current = workState;
      return;
    }

    if (
      prevWorkState === TASK_CONST.TASK_FRAME_WORKSTATE.PREWORK &&
      workState === TASK_CONST.TASK_FRAME_WORKSTATE.WORKING
    ) {
      workSessionRef.current = {
        firstActionTime: null,
        startTime: Date.now(),
        thresholdSecond: THRESHOLD_SECOND.WORK,
        workType: TASK_CONST.TASK_FRAME_WORK_TYPE.WORK,
      };
    }

    if (
      prevWorkState === TASK_CONST.TASK_FRAME_WORKSTATE.REJECT &&
      workState === TASK_CONST.TASK_FRAME_WORKSTATE.WORKING
    ) {
      workSessionRef.current = {
        firstActionTime: null,
        startTime: Date.now(),
        thresholdSecond: THRESHOLD_SECOND.REWORK,
        workType: TASK_CONST.TASK_FRAME_WORK_TYPE.REWORK,
      };
    }

    if (workState === TASK_CONST.TASK_FRAME_WORKSTATE.REVIEWING) {
      if (prevWorkState !== TASK_CONST.TASK_FRAME_WORKSTATE.REVIEWING) {
        workSessionRef.current = {
          firstActionTime: null,
          startTime: Date.now(),
          thresholdSecond: THRESHOLD_SECOND.REVIEW,
          workType: TASK_CONST.TASK_FRAME_WORK_TYPE.REVIEW,
        };
      }
    }

    if (
      (isReviewTerminalTransition(prevWorkState, workState) ||
        isSubmitTransition(prevWorkState, workState)) &&
      workSessionRef.current
    ) {
      flushWorkTime({
        reason: 'stateTransition',
        frameSeq,
        taskID,
      });
    }

    prevWorkStateRef.current = workState;
  }, [currentFrame, updateWorkTime]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const frameSeq = prevFrameSeqRef.current;
      const taskID = prevTaskIDRef.current;
      const prevWorkState = prevWorkStateRef.current;

      if (frameSeq == null || taskID == null) return;
      if (
        prevWorkState !== TASK_CONST.TASK_FRAME_WORKSTATE.WORKING &&
        prevWorkState !== TASK_CONST.TASK_FRAME_WORKSTATE.REVIEWING
      ) {
        return;
      }

      flushWorkTime({
        reason: 'beforeUnload',
        frameSeq,
        taskID,
      });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [updateWorkTime]);

  useEffect(() => {
    return () => {
      const currentFrame = useTaskFrameStore.getState().currentFrame;

      if (currentFrame == null) return;

      flushWorkTime({
        reason: 'unmount',
        frameSeq: currentFrame.frameSeq,
        taskID: currentFrame.taskID,
      });
    };
  }, []);
};

export default useWorkTimeTracker;
